import { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import React from 'react';

interface FlyoutContextValue {
  registerFlyout: (id: string, close: () => void) => void;
  unregisterFlyout: (id: string) => void;
  closeOtherFlyouts: (id: string) => void;
}

const FlyoutContext = createContext<FlyoutContextValue>({
  registerFlyout: () => {},
  unregisterFlyout: () => {},
  closeOtherFlyouts: () => {}
});

export function FlyoutProvider({ children }: { children: React.ReactNode }) {
  const flyouts = useRef(new Map<string, () => void>());

  const registerFlyout = useCallback((id: string, close: () => void) => {
    flyouts.current.set(id, close);
  }, []);

  const unregisterFlyout = useCallback((id: string) => {
    flyouts.current.delete(id);
  }, []);

  const closeOtherFlyouts = useCallback((id: string) => {
    flyouts.current.forEach((close, key) => {
      if (key !== id) close();
    });
  }, []);

  const value = useMemo(
    () => ({ registerFlyout, unregisterFlyout, closeOtherFlyouts }),
    [registerFlyout, unregisterFlyout, closeOtherFlyouts]
  );

  return (
    <FlyoutContext.Provider value={value}>{children}</FlyoutContext.Provider>
  );
}

export function useFlyoutContext() {
  return useContext(FlyoutContext);
}
